import PropTypes from 'prop-types';
import {
  Text,
  Box,
  Textarea,
  Button,
  Input,
  Stack,
  Checkbox,
  IconButton,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Flex,
  Spacer,
  ListItem,
  UnorderedList,
  VStack,
} from '@chakra-ui/react';

import ReactQuillEditor from 'components/common/ReactQuillEditor';

import { IoMdClose } from 'react-icons/io';

import { useEffect, useState } from 'react';
import FormikFormControl from 'components/common/forms/FormikFormControl';
import { calculateContentSize } from 'utils/react-quill/quillConfig';
/* eslint-enable import/no-unresolved */
function MultipleChoiceInput({ question, onUpdate }) {
  const [newQuestion, setNewQuestion] = useState({
    id: question.id,
    tempId: question.tempId || null,
    questionHtml: question.questionHtml,
    questionTypeId: question.questionTypeId,
    multipleChoiceChoices: question.multipleChoiceChoices || [],
    multipleChoiceAnswers: question.multipleChoiceAnswers || [],
    trueOrFalseAnswer: question.trueOrFalseAnswer || true,
    explanation: question.explanation || '',
    mark: question.mark,
    allowPartialPoints: question.allowPartialPoints,
    errors: question.errors,
    autoCheckInstruction: question.autoCheckInstruction || '',
    isAllowedAutoCheck: question.isAllowedAutoCheck || true,
  });

  // Initial validation on mount and when question prop changes
  useEffect(() => {
    const errors = [];

    // Question text required
    if (
      !newQuestion.questionHtml ||
      newQuestion.questionHtml.trim() === '' ||
      newQuestion.questionHtml === '<p><br></p>'
    ) {
      errors.push('Question cannot be blank. ');
    }

    // Question content size validation
    if (newQuestion.questionHtml) {
      const questionSize = calculateContentSize(newQuestion.questionHtml);
      if (questionSize > 3) {
        errors.push('Question content size exceeds 3MB limit. ');
      }
    }

    // At least 2 choices
    if (
      !Array.isArray(newQuestion.multipleChoiceChoices) ||
      newQuestion.multipleChoiceChoices.length < 2
    ) {
      errors.push('At least 2 choices are required. ');
    }
    // No blank choices
    if (
      Array.isArray(newQuestion.multipleChoiceChoices) &&
      newQuestion.multipleChoiceChoices.some(
        (c) => !c.value || c.value.trim() === ''
      )
    ) {
      errors.push('Choices cannot be blank. ');
    }

    // At least 1 answer
    if (
      !Array.isArray(newQuestion.multipleChoiceAnswers) ||
      newQuestion.multipleChoiceAnswers.length === 0
    ) {
      errors.push('Answer/s are required. ');
    }
    // Mark must be positive
    if (typeof newQuestion.mark !== 'number' || newQuestion.mark <= 0) {
      errors.push('Mark must be a positive number. ');
    }

    setNewQuestion((prev) => ({
      ...prev,
      errors,
    }));

    if (onUpdate) {
      onUpdate({
        ...newQuestion,
        errors,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    newQuestion.questionHtml,
    newQuestion.multipleChoiceChoices,
    newQuestion.multipleChoiceAnswers,
    newQuestion.mark,
  ]);

  const setQuestionAnswers = (ans) => {
    if (ans != null) {
      const newMultipleChoiceAnswers = question.multipleChoiceChoices.filter(
        (item) => ans.includes(item.option)
      );

      let newErrors = [...newQuestion.errors];
      const errorMultipleChoiceChoices = 'Answer/s are required. ';
      if (newMultipleChoiceAnswers.length === 0) {
        if (!newErrors.includes(errorMultipleChoiceChoices)) {
          newErrors.push(errorMultipleChoiceChoices);
        }
      } else {
        newErrors = newErrors.filter((e) => e !== errorMultipleChoiceChoices);
      }

      const updatedQuestion = {
        ...newQuestion,
        multipleChoiceAnswers: newMultipleChoiceAnswers,
        errors: newErrors,
      };

      setNewQuestion(updatedQuestion);

      if (onUpdate) {
        onUpdate(updatedQuestion);
      }
    }
  };

  const handleAnswerChange = (event) => {
    const { value, checked } = event.target;
    let ans = [...newQuestion.multipleChoiceAnswers.map((a) => a.option)];

    if (checked) {
      if (!ans.includes(value)) {
        ans.push(value);
      }
    } else {
      ans = ans.filter((item) => item !== value);
    }
    setQuestionAnswers(ans);
  };

  const sortChoices = (choices) => {
    return choices.sort((a, b) => a.option.localeCompare(b.option));
  };

  const reassignOptions = (choices) => {
    return choices.map((choice, index) => {
      const newOption = String.fromCharCode(65 + index); // 65 is the char code for 'A'
      return { option: newOption, value: choice.value };
    });
  };

  const handleChoiceChange = (option, newValue) => {
    const cleanValue = newValue.replace(/[\r\n\t]/g, ' '); // Replace newlines, carriage returns, tabs with space
    const newMultipleChoiceChoices = newQuestion.multipleChoiceChoices.map(
      (c) => (c.option === option ? { ...c, value: cleanValue } : c)
    );
    const newMultipleChoiceAnswers = newQuestion.multipleChoiceAnswers.map(
      (c) => (c.option === option ? { ...c, value: cleanValue } : c)
    );

    let newErrors = [...newQuestion.errors];
    const errorMultipleChoiceChoices = 'Choices cannot be blank. ';
    if (cleanValue.trim() === '') {
      if (!newErrors.includes(errorMultipleChoiceChoices)) {
        newErrors.push(errorMultipleChoiceChoices);
      }
    } else {
      newErrors = newErrors.filter((e) => e !== errorMultipleChoiceChoices);
    }

    setNewQuestion({
      ...newQuestion,
      multipleChoiceChoices: newMultipleChoiceChoices,
      multipleChoiceAnswers: newMultipleChoiceAnswers,
      errors: newErrors,
    });
  };

  const handleAddChoice = () => {
    const currentOptions = newQuestion.multipleChoiceChoices.map(
      (choice) => choice.option
    );
    let nextOptionCharCode = 65; // ASCII code for 'A'

    while (currentOptions.includes(String.fromCharCode(nextOptionCharCode))) {
      nextOptionCharCode += 1;
    }
    const nextOption = String.fromCharCode(nextOptionCharCode);
    const newMultipleChoiceChoices = [
      ...newQuestion.multipleChoiceChoices,
      { option: nextOption, value: nextOption },
    ].sort((a, b) => a.option.localeCompare(b.option));

    let newErrors = [...newQuestion.errors];
    const errorMultipleChoiceChoices = 'Choices are required. ';
    if (newMultipleChoiceChoices.length === 0) {
      if (!newErrors.includes(errorMultipleChoiceChoices)) {
        newErrors.push(errorMultipleChoiceChoices);
      }
    } else {
      newErrors = newErrors.filter((e) => e !== errorMultipleChoiceChoices);
    }

    setNewQuestion({
      ...newQuestion,
      multipleChoiceChoices: newMultipleChoiceChoices,
      errors: newErrors,
    });
  };

  const handleRemoveChoice = (optionToRemove) => {
    const newMultipleChoiceChoices = reassignOptions(
      sortChoices(
        newQuestion.multipleChoiceChoices.filter(
          (choice) => choice.option !== optionToRemove
        )
      )
    );
    const newMultipleChoiceAnswers = sortChoices(
      newQuestion.multipleChoiceAnswers.filter(
        (choice) => choice.option !== optionToRemove
      )
    );
    let newErrors = [...newQuestion.errors];
    const errorMultipleChoiceChoices = 'Choices are required. ';
    if (newMultipleChoiceChoices.length === 0) {
      if (!newErrors.includes(errorMultipleChoiceChoices)) {
        newErrors.push(errorMultipleChoiceChoices);
      }
    } else {
      newErrors = newErrors.filter((e) => e !== errorMultipleChoiceChoices);
    }

    setNewQuestion({
      ...newQuestion,
      multipleChoiceChoices: newMultipleChoiceChoices,
      multipleChoiceAnswers: newMultipleChoiceAnswers,
      errors: newErrors,
    });
  };

  const handleExplanationChange = (newValue) => {
    const updatedQuestion = {
      ...newQuestion,
      explanation: newValue,
    };

    setNewQuestion(updatedQuestion);

    // Notify parent component of the change
    if (onUpdate) {
      onUpdate(updatedQuestion);
    }
  };
  const handleQuestionChange = (content) => {
    let newErrors = [...newQuestion.errors];

    const questionError = 'Question cannot be blank. ';
    const sizeError = 'Question content size exceeds 3MB limit. ';

    // Check if content is empty
    if (!content || content.trim() === '' || content === '<p><br></p>') {
      if (!newErrors.includes(questionError)) {
        newErrors.push(questionError);
      }
    } else {
      newErrors = newErrors.filter((e) => e !== questionError);
    }

    // Check content size
    if (content) {
      const contentSize = calculateContentSize(content);
      if (contentSize > 3) {
        if (!newErrors.includes(sizeError)) {
          newErrors.push(sizeError);
        }
      } else {
        newErrors = newErrors.filter((e) => e !== sizeError);
      }
    } else {
      newErrors = newErrors.filter((e) => e !== sizeError);
    }

    const updatedQuestion = {
      ...newQuestion,
      questionHtml: content,
      errors: newErrors,
    };

    setNewQuestion(updatedQuestion);

    // Notify parent component of the change
    if (onUpdate) {
      onUpdate(updatedQuestion);
    }
  };

  const handleMarkChange = (newValue) => {
    let mark = 0;
    if (newValue !== '' && !Number.isNaN(newValue)) {
      mark = parseFloat(newValue);
    }

    const updatedQuestion = {
      ...newQuestion,
      mark,
    };

    setNewQuestion(updatedQuestion);

    // Notify parent component of the change
    if (onUpdate) {
      onUpdate(updatedQuestion);
    }
  };

  const handlePartialPointChange = (checked) => {
    const updatedQuestion = {
      ...newQuestion,
      allowPartialPoints: checked,
    };

    setNewQuestion(updatedQuestion);

    // Notify parent component of the change
    if (onUpdate) {
      onUpdate(updatedQuestion);
    }
  };

  return (
    <VStack gap={4} align="stretch">
      <Flex align="center">
        {newQuestion.errors.length > 0 && (
          <Box color="red.500">
            <UnorderedList>
              {newQuestion.errors.map((err) => (
                <ListItem key={err}>{err}</ListItem>
              ))}
            </UnorderedList>
          </Box>
        )}
      </Flex>

      <FormikFormControl label="Question">
        <ReactQuillEditor
          value={newQuestion.questionHtml}
          onChange={handleQuestionChange}
          placeholder="Enter question text..."
          maxSizeInMB={3}
        />
      </FormikFormControl>

      <Flex align="center">
        <Box mr={3}>
          <Text>Mark</Text>
        </Box>
        <Box>
          <NumberInput
            min={0}
            max={100}
            w="100px"
            onChange={(e) => handleMarkChange(e)}
            value={newQuestion.mark}
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        </Box>
        <Spacer />
        <Box>
          <Checkbox
            size="lg"
            mr={5}
            onChange={(e) => handlePartialPointChange(e.target.checked)}
            isChecked={newQuestion.allowPartialPoints}
          >
            Allow partial points
          </Checkbox>
        </Box>
      </Flex>

      <Box>
        {newQuestion.multipleChoiceChoices.map((choice) => (
          <Stack
            key={choice.option}
            direction={['column', 'row']}
            spacing="24px"
            align="center"
            m={2}
          >
            <Box>
              <Text fontSize="lg">{choice.option}</Text>
            </Box>
            <Box w="50%">
              <Input
                value={choice.value}
                onChange={(e) =>
                  handleChoiceChange(choice.option, e.target.value)
                }
              />
            </Box>
            <Box>
              <Checkbox
                size="lg"
                value={choice.option}
                isChecked={newQuestion.multipleChoiceAnswers
                  .map((a) => a.option)
                  .includes(choice.option)}
                onChange={handleAnswerChange}
              />
            </Box>
            <Box>
              <IconButton
                isRound
                size="sm"
                icon={<IoMdClose />}
                onClick={() => handleRemoveChoice(choice.option)}
              />
            </Box>
          </Stack>
        ))}

        <Box mt={4}>
          <Button
            colorScheme="blue"
            size="sm"
            variant="outline"
            onClick={() => handleAddChoice()}
          >
            Add Choice
          </Button>
        </Box>
      </Box>

      <FormikFormControl label="Explanation">
        <Textarea
          value={newQuestion.explanation}
          onChange={(e) => handleExplanationChange(e.target.value)}
        />
      </FormikFormControl>
    </VStack>
  );
}

MultipleChoiceInput.propTypes = {
  question: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

export default MultipleChoiceInput;
